/**
 * The live Portfolio Segment store. Holds the slice the user is building so the panel,
 * the loan list, and the live count all read one source. Persisted to localStorage for
 * instant paint on reload; the SERVER draft (see use-segment) is the cross-device source
 * of truth and hydrates over this on mount, after which edits autosave back.
 *
 * NAMESPACED PER ORGANIZATION (plan §8-F7): the persisted drafts live in a
 * `byOrg[organizationId]` map and every read/write goes through the ACTIVE org id
 * (bound by `useSegmentSync` from the current-organization query). Until the org id is
 * known the store reads as the empty segment and drops edits — so switching tenants can
 * never flash (or autosave!) bank A's slice onto bank B's screen, and each org's draft
 * is restored when you switch back.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import {
  EMPTY_SEGMENT,
  SEGMENT_VERSION,
  type FieldFilter,
  type Segment,
  type SegmentReadiness,
} from '@repo/shared/common';

interface SegmentState {
  /** The active organization id (bound from the current-org query; null until known).
   *  NOT persisted — each page binds it fresh, so a stale org can never leak in. */
  readonly organizationId: string | null;
  /** Per-org segment drafts (only the orgs this browser has edited). Persisted. */
  readonly byOrg: Readonly<Record<string, Segment>>;
  /** Bind the active organization (idempotent; changing org switches the visible draft). */
  readonly setOrganization: (organizationId: string | null) => void;
  /** Upsert one field's filter (replace the filter for its key, or append). */
  readonly setFieldFilter: (filter: FieldFilter) => void;
  /** Drop a field's filter entirely. */
  readonly removeField: (key: string) => void;
  /** Merge a readiness/transaction toggle patch. */
  readonly setReadiness: (patch: Partial<SegmentReadiness>) => void;
  /** Clear back to the whole portfolio. */
  readonly reset: () => void;
  /** Replace the whole segment (server-draft hydration; does not itself autosave). */
  readonly hydrate: (segment: Segment) => void;
}

type PersistedFields = Pick<SegmentState, 'byOrg'>;

function upsertFilter(fields: readonly FieldFilter[], filter: FieldFilter): FieldFilter[] {
  const next = fields.filter((f) => f.key !== filter.key);
  next.push(filter);
  return next;
}

/** The active org's segment out of the map (empty until the org is bound). */
function segmentOf(state: Pick<SegmentState, 'organizationId' | 'byOrg'>): Segment {
  if (state.organizationId === null) return EMPTY_SEGMENT;
  return state.byOrg[state.organizationId] ?? EMPTY_SEGMENT;
}

export const useSegmentStore = create<SegmentState>()(
  persist(
    (set) => {
      /** Apply `update` to the ACTIVE org's segment; a no-op until the org is bound
       *  (an edit with no tenant to attribute it to must never be persisted). */
      const updateSegment = (update: (segment: Segment) => Segment): void => {
        set((state) => {
          if (state.organizationId === null) return state;
          return {
            byOrg: { ...state.byOrg, [state.organizationId]: update(segmentOf(state)) },
          };
        });
      };

      return {
        organizationId: null,
        byOrg: {},
        setOrganization: (organizationId) => set({ organizationId }),
        setFieldFilter: (filter) =>
          updateSegment((segment) => ({
            ...segment,
            fields: upsertFilter(segment.fields, filter),
          })),
        removeField: (key) =>
          updateSegment((segment) => ({
            ...segment,
            fields: segment.fields.filter((f) => f.key !== key),
          })),
        setReadiness: (patch) =>
          updateSegment((segment) => ({
            ...segment,
            readiness: { ...segment.readiness, ...patch },
          })),
        reset: () => updateSegment(() => EMPTY_SEGMENT),
        hydrate: (segment) => updateSegment(() => segment),
      };
    },
    {
      name: 'portfolio-segment',
      version: 2,
      partialize: (state): PersistedFields => ({ byOrg: state.byOrg }),
      migrate(persisted, version) {
        // v1 persisted a single un-namespaced `segment` that cannot be attributed to an
        // org — discard it (the server draft re-hydrates the right slice per org).
        if (version < 2) return { byOrg: {} } as PersistedFields;
        const state = persisted as Partial<PersistedFields>;
        return { byOrg: state.byOrg ?? {} } as PersistedFields;
      },
    },
  ),
);

/** The active org's live segment (empty until the org binding lands). References are
 *  stable (the stored object or the EMPTY_SEGMENT constant), so no shallow compare. */
export const useSegment = (): Segment => useSegmentStore((state) => segmentOf(state));

/** The segment store's actions, as a stable object. */
export const useSegmentActions = (): Pick<
  SegmentState,
  'setOrganization' | 'setFieldFilter' | 'removeField' | 'setReadiness' | 'reset' | 'hydrate'
> =>
  useSegmentStore(
    useShallow((state) => ({
      setOrganization: state.setOrganization,
      setFieldFilter: state.setFieldFilter,
      removeField: state.removeField,
      setReadiness: state.setReadiness,
      reset: state.reset,
      hydrate: state.hydrate,
    })),
  );

export { SEGMENT_VERSION };
